import React from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { Outlet, useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminMobileDrawer from './AdminMobileDrawer';
import AdminTopbar from './AdminTopbar';
import AdminFooter from './AdminFooter';
import useInactivityLogout from '../../hooks/useInactivityLogout';
import { adminLayout } from '../../../theme/foundations/adminPanel';

/**
 * Chrome compartido por las cuatro secciones administrativas protegidas.
 * Cada ruta hija se monta en `<Outlet />`, así que la URL cambia de
 * verdad al navegar en vez de intercambiar componentes en estado local.
 *
 * `AdminSidebar` es `position: fixed` (ver ese componente), así que sale
 * del flujo normal: el contenido principal compensa con
 * `margin-left: 208px` y `width: calc(100% - 208px)` desde `lg`, y sin
 * margen ni recorte por debajo (ahí el sidebar no se renderiza; lo
 * sustituye `AdminMobileDrawer`).
 */
export default function AdminLayout() {
  const navigate = useNavigate();
  const mobileMenu = useDisclosure();
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    return localStorage.getItem('siesa_admin_sidebar_collapsed') === 'true';
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('siesa_admin_sidebar_collapsed', String(next));
      return next;
    });
  };

  // ProtectedAdminRoute solo comprueba la sesión al montar: si expira por
  // inactividad mientras el admin ya está dentro, nadie más lo redirige.
  useInactivityLogout(() => navigate('/admin/login', { replace: true }));

  const sidebarWidth = isCollapsed ? '68px' : adminLayout.sidebarWidth;

  return (
    <Box minH="100dvh" bg="adminPanel.bg" overflowX="hidden">
      <AdminSidebar isCollapsed={isCollapsed} onToggleCollapse={toggleCollapse} />
      <AdminMobileDrawer isOpen={mobileMenu.isOpen} onClose={mobileMenu.onClose} />

      <Flex
        direction="column"
        minH="100dvh"
        minW={0}
        ml={{ base: 0, lg: sidebarWidth }}
        w={{ base: 'full', lg: `calc(100% - ${sidebarWidth})` }}
        transition="margin-left 0.2s ease-in-out, width 0.2s ease-in-out"
      >
        <AdminTopbar onOpenMobileMenu={mobileMenu.onOpen} />
        <Box
          as="main"
          flex="1"
          minW={0}
          w="full"
          maxW={adminLayout.contentMaxWidth}
          mx="auto"
          px={{ base: adminLayout.contentPaddingMobile, lg: adminLayout.contentPadding }}
          py={{ base: adminLayout.contentPaddingMobile, lg: adminLayout.contentPadding }}
          minH={`calc(100dvh - ${adminLayout.topbarHeight})`}
          overflowY="auto"
          overflowX="hidden"
        >
          <Outlet />
        </Box>
        <AdminFooter />
      </Flex>
    </Box>
  );
}
